/**
 * The customer's first step on the Collection and Credit tabs: initiate the
 * collection with a proposed date, and initiate the credit with an amount and
 * reason.
 *
 * Restored 19 Oct 2026: the spec flow (QUALITY-NCR-REVIEW, 2 Oct) removed both
 * forms and let the supplier start each process instead. The forms
 * are the ones development had before it (CR-6073 required date, CR-7352
 * optional owner).
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { CreditCard, Loader2, Package } from 'lucide-react';
import type { NCRCollectionAddress } from '../../lib/api';
import { CollectionAssigneeSelect, UNASSIGNED, useCollectionAssignees } from './CollectionAssignment';
import { MoneyInput } from './ClaimInputs';
import { isPastDate, moneyError, type MoneyRule } from './claimLimits';
import { todayLocalISO } from './localDate';

export interface InitiateCollectionData extends NCRCollectionAddress {
  proposed_collection_date?: string;
  /** Free-text address an older 'requested' row holds; passed back unchanged. */
  collection_address?: string;
  collection_notes?: string;
  customer_notes?: string;
  /** CR-7352: optional owner chosen on the Initiate form. */
  assigned_to?: number | null;
}

interface InitiateCollectionFormProps {
  ncrId: number;
  saving: boolean;
  /** False once an owner was given before initiating -- the assignment panel owns it then. */
  showAssignee: boolean;
  /** What an older 'requested' row already holds, so the form doesn't start blank. */
  initial?: NCRCollectionAddress & {
    proposed_collection_date?: string | null; collection_address?: string; customer_notes?: string;
  };
  onInitiate: (data: InitiateCollectionData) => void;
}

/** CR-8719: the full collection address, in form order. */
const ADDRESS_INPUTS: ReadonlyArray<{ key: keyof NCRCollectionAddress; label: string; placeholder: string }> = [
  { key: 'collection_address_line_1', label: 'Address Line 1', placeholder: 'Building, street' },
  { key: 'collection_address_line_2', label: 'Address Line 2', placeholder: 'Unit, area (optional)' },
  { key: 'collection_city', label: 'Town / City', placeholder: 'Town or city' },
  { key: 'collection_county', label: 'County / State', placeholder: 'County or state' },
  { key: 'collection_postcode', label: 'Postcode', placeholder: 'Postcode / ZIP' },
  { key: 'collection_country', label: 'Country', placeholder: 'Country' },
];

/**
 * Customer: initiate the collection. The proposed date is required (CR-6073);
 * the supplier then confirms it.
 *
 * @param ncrId - the NCR being viewed (loads the assignee list)
 * @param saving - a request is in flight
 * @param showAssignee - offer the optional owner picker
 * @param initial - values an older 'requested' row already holds
 * @param onInitiate - posts the initiate request
 */
export function InitiateCollectionForm({ ncrId, saving, showAssignee, initial, onInitiate }: InitiateCollectionFormProps) {
  const [form, setForm] = useState<InitiateCollectionData>({
    proposed_collection_date: initial?.proposed_collection_date ?? '',
    collection_address: initial?.collection_address ?? '',
    customer_notes: initial?.customer_notes ?? '',
    ...Object.fromEntries(ADDRESS_INPUTS.map(({ key }) => [key, initial?.[key] ?? ''])),
  });
  // '' is "unassigned" -- SearchableSelect's own convention for nothing selected.
  const [assignee, setAssignee] = useState<string>(UNASSIGNED);
  const [dateError, setDateError] = useState<string | null>(null);
  const { assignees, loading, error } = useCollectionAssignees(ncrId, showAssignee);

  const handleInitiate = () => {
    if (!form.proposed_collection_date) {
      setDateError('Please enter a proposed collection date.');
      return;
    }
    // CR-8720: a collection is planned, so the date is today or later.
    if (isPastDate(form.proposed_collection_date, todayLocalISO())) {
      setDateError('Proposed collection date can’t be in the past.');
      return;
    }
    setDateError(null);
    onInitiate({
      ...form,
      ...(showAssignee ? { assigned_to: assignee === UNASSIGNED ? null : parseInt(assignee, 10) } : {}),
    });
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium text-sm">Initiate Collection</h4>
      <p className="text-sm text-muted-foreground">
        Defective parts need to be collected/returned. Start the collection process below.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="proposed_collection_date">Proposed Collection Date <span className="text-red-500">*</span></Label>
          <Input
            id="proposed_collection_date"
            type="date"
            min={todayLocalISO()}
            value={form.proposed_collection_date}
            onChange={e => {
              const v = e.target.value;
              setForm(f => ({ ...f, proposed_collection_date: v }));
              if (v) setDateError(null);
            }}
            aria-invalid={!!dateError}
          />
          {dateError && <p className="text-sm text-red-500">{dateError}</p>}
        </div>
      </div>
      {/* CR-8719: a full address, not one free-text box. */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Collection Address</legend>
        <p className="text-xs text-muted-foreground">Where should the parts be collected from?</p>
        <div className="grid grid-cols-2 gap-4">
          {ADDRESS_INPUTS.map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={key}>{label}</Label>
              <Input
                id={key}
                value={form[key] ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  setForm(f => ({ ...f, [key]: v }));
                }}
                placeholder={placeholder}
              />
            </div>
          ))}
        </div>
      </fieldset>
      {showAssignee && (
        <div className="space-y-2 w-72 max-w-full">
          {/* CR-7352: optional at initiation -- a collection nobody has been
              given still works exactly as it did before. */}
          <Label htmlFor="collection-initial-assignee">Assign to (optional)</Label>
          <CollectionAssigneeSelect
            id="collection-initial-assignee"
            value={assignee}
            onChange={setAssignee}
            assignees={assignees}
            loading={loading}
            error={error}
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground">
            They get a notification and the collection appears in their My Collections queue.
          </p>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="collection_customer_notes">Notes</Label>
        <Textarea
          id="collection_customer_notes"
          value={form.customer_notes}
          onChange={e => setForm(f => ({ ...f, customer_notes: e.target.value }))}
          placeholder="Instructions for the supplier..."
          rows={3}
        />
      </div>
      <Button onClick={handleInitiate} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
        Initiate Collection
      </Button>
    </div>
  );
}

export interface InitiateCreditData {
  credit_amount?: number;
  credit_currency?: string;
  credit_reason?: string;
  customer_notes?: string;
}

interface InitiateCreditFormProps {
  saving: boolean;
  /** CR-8720: the currency to state the amount in and the order-value cap. */
  rule: MoneyRule;
  onInitiate: (data: InitiateCreditData) => void;
}

/**
 * Customer: initiate the credit. The supplier then enters the credit note.
 *
 * @param saving - a request is in flight
 * @param rule - currency and order-value cap for the amount (CR-8720)
 * @param onInitiate - posts the initiate request
 */
export function InitiateCreditForm({ saving, rule, onInitiate }: InitiateCreditFormProps) {
  const [form, setForm] = useState({ credit_amount: '', credit_reason: '', customer_notes: '' });
  const [amountError, setAmountError] = useState<string | null>(null);

  const handleInitiate = () => {
    // CR-8720: 0.00 or more, two decimals, within the order value (the server checks too).
    const problem = moneyError(form.credit_amount, rule, 'Credit amount');
    if (problem) { setAmountError(problem); return; }
    setAmountError(null);
    onInitiate({
      credit_amount: form.credit_amount ? parseFloat(form.credit_amount) : undefined,
      credit_currency: rule.currency,
      credit_reason: form.credit_reason,
      customer_notes: form.customer_notes,
    });
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium text-sm">Initiate Credit Process</h4>
      <p className="text-sm text-muted-foreground">
        A credit note is required for this NCR. Start the credit process below.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <MoneyInput
            id="initiate_credit_amount"
            label="Credit Amount"
            value={form.credit_amount}
            onChange={v => setForm(f => ({ ...f, credit_amount: v }))}
            rule={rule}
            onValidity={setAmountError}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="initiate_credit_reason">Credit Reason</Label>
          <Input
            id="initiate_credit_reason"
            value={form.credit_reason}
            onChange={e => setForm(f => ({ ...f, credit_reason: e.target.value }))}
            placeholder="Reason for credit request"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="initiate_credit_notes">Notes</Label>
        <Textarea
          id="initiate_credit_notes"
          value={form.customer_notes}
          onChange={e => setForm(f => ({ ...f, customer_notes: e.target.value }))}
          placeholder="Additional details..."
          rows={3}
        />
      </div>
      <Button onClick={handleInitiate} disabled={saving || !!amountError}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
        Initiate Credit
      </Button>
    </div>
  );
}
